import * as React from 'react'
import { observer } from 'mobx-react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetTitle,
} from 'components/ModalSheet'
import {
  Form,
  FormRow,
  NumericInputContainer,
  SelectContainer,
} from 'components/inputs'
import Button from 'components/Button'
import { IconButton } from 'components/IconButton'
import Icon from 'components/Icon'
import {
  EstimatedIrsPaymentDialogViewModel,
  StateEstimatedTaxPaymentEntry,
} from './EstimatedIrsPaymentDialogViewModel'
import { US_STATES } from 'consts/us-states'
import styles from './EstimatedIrsPaymentDialog.sass'

interface Props {
  vm: EstimatedIrsPaymentDialogViewModel
}

export default observer(function EstimatedIrsPaymentDialog({ vm }: Props) {
  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.close} disableCloseOnOutsideClick>
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Extension Accepted</ModalSheetTitle>
      </ModalSheetHeader>

      <ModalSheetContent>
        <ModalSheetContentBlock expanded>
          <Form>
            <FormRow>
              <NumericInputContainer
                model={vm}
                precision={2}
                field="federalAmount"
                name="federalAmount"
                label="Estimated federal tax payment"
                data-test="estimated-tax-payment-federal-amount"
              />
            </FormRow>

            {vm.stateEntries.map((entry, index) => (
              <StateEntryRow
                key={entry.id}
                entry={entry}
                index={index}
                onRemove={() => vm.removeStateEntry(index)}
              />
            ))}

            <FormRow>
              <Button
                type="secondary"
                size="wide-slim"
                onClick={vm.addStateEntry}
                data-test="estimated-tax-payment-add-state"
              >
                Add State
              </Button>
            </FormRow>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button type="default" onClick={vm.close}>
            Cancel
          </Button>
          <Button
            type="primary"
            data-test="estimated-tax-payment-submit-button"
            loading={vm.submit.pending}
            disabled={vm.submit.pending || vm.hasIncompleteStateEntries}
            onClick={vm.submit}
          >
            Accept Extension
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface StateEntryRowProps {
  entry: StateEstimatedTaxPaymentEntry
  index: number
  onRemove: () => void
}

const StateEntryRow = observer(function StateEntryRow({
  entry,
  index,
  onRemove,
}: StateEntryRowProps) {
  return (
    <FormRow>
      <div className={styles.stateEntryRow}>
        <div className={styles.stateEntryField}>
          <SelectContainer
            model={entry}
            field="state"
            items={US_STATES}
            label="State"
            placeholder="Select a state"
            data-test={`estimated-tax-payment-state-name-${index}`}
          />
        </div>
        <div className={styles.stateEntryField}>
          <NumericInputContainer
            model={entry}
            precision={2}
            field="amount"
            name={`state-amount-${index}`}
            label="Amount"
            data-test={`estimated-tax-payment-state-amount-${index}`}
          />
        </div>
        <div className={styles.stateEntryRemove}>
          <IconButton
            onClick={onRemove}
            dataTest={`estimated-tax-payment-remove-state-${index}`}
          >
            <Icon name="close" />
          </IconButton>
        </div>
      </div>
    </FormRow>
  )
})
